<template>
  <div class="contact-search">
    <v-layout row wrap>

      <!-- intro !-->
      <v-flex xs12>
        <FormSectionTitle>
          Your <span class="text-capitalize">{{displayAccountType}}</span>
          <template
            v-if="isRealEstateProfessional
            && this.$store.state.unleash.enabledFeatures['WEB-4892-Require-Customer-ScheduleNow']"
            v-slot:suffix>
            (required)
          </template>
          <template v-else v-slot:suffix>
            (optional)
          </template>
        </FormSectionTitle>

        <span>
          Adding your {{displayAccountType}} means they will receive
          your inspection report too.
        </span>
      </v-flex>

      <!-- content !-->
      <v-flex xs12>
        <ContactDetailsBox v-if="appointmentContact" :contact="appointmentContact">
          <div class="remove-container">
            <a class="remove" @click="removeContact">Remove</a>
          </div>
        </ContactDetailsBox>

        <div v-else class="contact-search--content">
          <!-- add account button !-->
          <ButtonCommon v-if="!addContact" border @click="clickAddButton">
            + Add {{displayAccountType}}
          </ButtonCommon>

          <template v-if="addContact">
            <!-- search form !-->
            <ContactSearchForm
              v-if="searchContact"
              :apiError="searchError"
              :email="searchEmail"
              :loading="searchLoading"
              @submit="searchAccount" />

            <!-- account create form !-->
            <template v-if="createContact">
              <BaseAlert hg-type="info" :hg-value="true">
                  We looked, but we didn't find an {{createAlertContactLabel}}
                  with that email address. Please enter their information below.
              </BaseAlert>

              <ContactCreateForm
                :email="searchEmail"
                :loading="createLoading"
                :type="contactType"
                @editEmail="handleEditEmail"
                @submit="createAccount" />
            </template>
          </template>
        </div>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import { Components, Constants } from '@homegauge/vue';
import { mapActions, mapGetters, mapState } from 'vuex';

import ContactDetailsBox from '@/components/ContactDetailsBox.vue';
import ButtonCommon from '@/components/common/ButtonCommon.vue';
import ContactCreateForm from '@/components/forms/ContactCreateForm.vue';
import ContactSearchForm from '@/components/forms/ContactSearchForm.vue';
import FormSectionTitle from '@/components/FormSectionTitle.vue';

const { BaseAlert } = Components;
const { AccountTypes } = Constants;

export default {
  components: {
    BaseAlert,
    ButtonCommon,
    ContactCreateForm,
    ContactDetailsBox,
    ContactSearchForm,
    FormSectionTitle,
  },
  data() {
    return {
      addContact: false,
      createContact: false,
      createError: null,
      createLoading: false,
      searchContact: false,
      searchEmail: '',
      searchError: null,
      searchLoading: false,
    };
  },
  computed: {
    ...mapState('Appointment', {
      appointmentContact: state => state.contactComponentData.contact,
    }),
    ...mapGetters('Auth', ['personType']),
    createAlertContactLabel() {
      switch (this.contactType) {
        case Constants.AccountTypes.REAL_ESTATE_PROFESSIONAL:
          return `an ${this.displayAccountType}`;
        case Constants.AccountTypes.CUSTOMER:
        default:
          return `a ${this.displayAccountType}`;
      }
    },
    contactType() {
      return this.personType === AccountTypes.CUSTOMER
        ? AccountTypes.REAL_ESTATE_PROFESSIONAL
        : AccountTypes.CUSTOMER;
    },
    isRealEstateProfessional() {
      return this.personType === AccountTypes.REAL_ESTATE_PROFESSIONAL;
    },
    displayAccountType() {
      switch (this.contactType) {
        case Constants.AccountTypes.REAL_ESTATE_PROFESSIONAL:
          return 'agent';
        case Constants.AccountTypes.CUSTOMER:
        default:
          return 'client';
      }
    },
  },
  methods: {
    ...mapActions('Appointment', ['setContact']),
    ...mapActions('Person', [
      'findCustomer',
      'findRep',
      'lookupEmail',
      'makeAccount',
    ]),
    clickAddButton() {
      this.addContact = true;
      this.searchContact = true;
    },
    async createAccount(accountPayload) {
      this.createLoading = true;
      this.createError = null;

      try {
        const foundUser = await this.lookupEmail({
          email: accountPayload.email,
          type: accountPayload.type,
        });

        const createdUser = await this.makeAccount({
          ...accountPayload,
          accountType: accountPayload.type,
          token: foundUser.token,
        });
        await this.setContact(createdUser);
      } catch (err) {
        this.createError = err;
      }

      this.createLoading = false;
    },
    handleEditEmail() {
      this.createContact = false;
      this.searchContact = true;
    },
    async removeContact() {
      await this.setContact(null);
    },
    async searchAccount(searchEmail) {
      this.searchEmail = searchEmail;
      this.searchError = null;
      this.searchLoading = true;

      try {
        const foundUser = this.personType === AccountTypes.CUSTOMER
          ? await this.findRep(searchEmail)
          : await this.findCustomer(searchEmail);
        if (foundUser && foundUser.exists) {
          await this.setContact(foundUser);
        } else {
          this.toggleCreateForm();
        }
      } catch (err) {
        this.searchError = err;
      }

      this.searchLoading = false;
    },
    toggleCreateForm() {
      this.createContact = true;
      this.searchContact = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/vars/theme';
@import '../styles/vars/typography';

.remove-container {
  text-align: right;

  .remove {
    color: $theme-color-secondary;
    font-size: 1rem;
    font-weight: $typography-weight-bold;
  }
}
</style>
